import { Outlet, createFileRoute } from '@tanstack/react-router'

import AppShell from '@/components/layout/AppShell'
import { prefetchCompanyLogos } from '@/features/market/api/companyLogos'
import { prefetchMarketRail } from '@/features/market-rail/api/queries'
import { appBeforeLoad } from '@/routes/layouts/app.guard'

/**
 * Everything that lives inside the signed-in app: header, sidebar, the lot.
 *
 * Mounted by `layout('app', ...)` in routes/app.ts, so it contributes no URL
 * segment — its children are still `/` and `/companies`.
 *
 * It is also the only gate on the signed-in app. Every screen behind a session
 * is a child of this route, so one `beforeLoad` covers `/watchlist`, `/profile`
 * and the twenty-odd others, and a screen added tomorrow is guarded the moment
 * it is listed in routes/app.ts. The auth screens and the onboarding funnel sit
 * outside this layout on purpose — app.guard.ts holds the guard and the
 * reasoning, in its own module so a test can mount it.
 */
export const Route = createFileRoute('/_app')({
  beforeLoad: appBeforeLoad,
  // The rail and market status, started as the app is entered and never
  // awaited — see `prefetchMarketRail`. Runs once per entry, not per screen:
  // navigating between two screens inside this layout leaves this match in
  // place, and a loader only re-runs for a match that is entered or reloaded.
  // Always after the guard above, which is what keeps it from firing for a
  // signed-out visitor: the router runs every `beforeLoad` in the branch
  // before any loader, and a redirect from one ends the load (a hover preload
  // included — it follows the redirect as a preload, and navigates nowhere).
  loader: ({ context }) => {
    prefetchMarketRail(context.queryClient)
    prefetchCompanyLogos(context.queryClient)
  },
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
})
